/* import __COLOCATED_TEMPLATE__ from './exceptions-editor.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { OfficeHoursExceptionData } from 'embercom/models/office-hours-schedule';
import generateUUID from 'embercom/lib/uuid-generator';

interface ExceptionFormData {
  id?: string;
  exception_date: string;
  exception_type: 'closed' | 'custom_hours';
  name: string;
  recurring_annually: boolean;
  time_intervals: Array<{ start_minute: number; end_minute: number }>;
}

interface Args {
  exceptions: OfficeHoursExceptionData[];
  onExceptionsChange: (exceptions: OfficeHoursExceptionData[]) => void;
}

export default class ExceptionsEditor extends Component<Args> {
  @service declare intl: IntlService;

  @tracked isAddingException = false;
  @tracked editingExceptionId: string | null = null;
  @tracked formData: ExceptionFormData = this.getDefaultFormData();

  get exceptions(): OfficeHoursExceptionData[] {
    return this.args.exceptions || [];
  }

  get exceptionTypeOptions() {
    return [
      {
        value: 'closed',
        text: this.intl.t('components.settings.office-hours.exceptions.type.closed'),
      },
      {
        value: 'custom_hours',
        text: this.intl.t('components.settings.office-hours.exceptions.type.custom-hours'),
      },
    ];
  }

  get isFormValid(): boolean {
    if (!this.formData.exception_date) {
      return false;
    }
    if (
      this.formData.exception_type === 'custom_hours' &&
      this.formData.time_intervals.length === 0
    ) {
      return false;
    }
    return true;
  }

  get sortedExceptions(): OfficeHoursExceptionData[] {
    return [...this.exceptions].sort(
      (a, b) => new Date(a.exception_date).getTime() - new Date(b.exception_date).getTime(),
    );
  }

  getDefaultFormData(): ExceptionFormData {
    return {
      exception_date: '',
      exception_type: 'closed',
      name: '',
      recurring_annually: false,
      time_intervals: [],
    };
  }

  @action
  startAddingException() {
    this.formData = this.getDefaultFormData();
    this.isAddingException = true;
    this.editingExceptionId = null;
  }

  @action
  startEditingException(exception: OfficeHoursExceptionData) {
    this.formData = {
      id: exception.id,
      exception_date: exception.exception_date,
      exception_type: exception.exception_type,
      name: exception.name || '',
      recurring_annually: exception.recurring_annually,
      time_intervals: [...exception.time_intervals],
    };
    this.editingExceptionId = exception.id;
    this.isAddingException = true;
  }

  @action
  cancelEditing() {
    this.isAddingException = false;
    this.editingExceptionId = null;
    this.formData = this.getDefaultFormData();
  }

  @action
  saveException() {
    if (!this.isFormValid) {
      return;
    }

    let updatedExceptions: OfficeHoursExceptionData[];

    if (this.editingExceptionId) {
      updatedExceptions = this.exceptions.map((exc) => {
        if (exc.id === this.editingExceptionId) {
          return {
            ...exc,
            exception_date: this.formData.exception_date,
            exception_type: this.formData.exception_type,
            name: this.formData.name || null,
            recurring_annually: this.formData.recurring_annually,
            time_intervals:
              this.formData.exception_type === 'closed' ? [] : this.formData.time_intervals,
          };
        }
        return exc;
      });
    } else {
      let newException: OfficeHoursExceptionData = {
        id: `temp-${generateUUID()}`,
        exception_date: this.formData.exception_date,
        exception_type: this.formData.exception_type,
        name: this.formData.name || null,
        recurring_annually: this.formData.recurring_annually,
        time_intervals:
          this.formData.exception_type === 'closed' ? [] : this.formData.time_intervals,
      };
      updatedExceptions = [...this.exceptions, newException];
    }

    this.args.onExceptionsChange(updatedExceptions);
    this.cancelEditing();
  }

  @action
  deleteException(exceptionId: string) {
    let updatedExceptions = this.exceptions.filter((exc) => exc.id !== exceptionId);
    this.args.onExceptionsChange(updatedExceptions);
  }

  @action
  updateFormField(field: keyof ExceptionFormData, event: Event) {
    let target = event.target as HTMLInputElement;
    this.formData = { ...this.formData, [field]: target.value };
  }

  @action
  updateExceptionType(value: 'closed' | 'custom_hours') {
    this.formData = {
      ...this.formData,
      exception_type: value,
      time_intervals: value === 'closed' ? [] : this.formData.time_intervals,
    };
  }

  @action
  addTimeInterval() {
    this.formData = {
      ...this.formData,
      time_intervals: [
        ...this.formData.time_intervals,
        { start_minute: 9 * 60, end_minute: 17 * 60 },
      ],
    };
  }

  @action
  updateTimeInterval(index: number, field: 'start_minute' | 'end_minute', value: number) {
    let newIntervals = [...this.formData.time_intervals];
    newIntervals[index] = { ...newIntervals[index], [field]: value };
    this.formData = { ...this.formData, time_intervals: newIntervals };
  }

  @action
  removeTimeInterval(index: number) {
    let newIntervals = this.formData.time_intervals.filter((_, i) => i !== index);
    this.formData = { ...this.formData, time_intervals: newIntervals };
  }

  @action
  formatDate(dateString: string): string {
    let date = new Date(dateString);
    return date.toLocaleDateString(this.intl.primaryLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  @action
  formatTimeInterval(interval: { start_minute: number; end_minute: number }): string {
    let formatTime = (minutes: number) => {
      let hours = Math.floor(minutes / 60);
      let mins = minutes % 60;
      let period = hours >= 12 ? 'pm' : 'am';
      let displayHours = hours;
      if (hours > 12) {
        displayHours = hours - 12;
      } else if (hours === 0) {
        displayHours = 12;
      }
      return `${displayHours}:${mins.toString().padStart(2, '0')}${period}`;
    };
    return `${formatTime(interval.start_minute)} - ${formatTime(interval.end_minute)}`;
  }

  formatTimeForInput(minutes: number): string {
    let hours = Math.floor(minutes / 60);
    let mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  @action
  updateTimeIntervalFromInput(index: number, field: 'start_minute' | 'end_minute', event: Event) {
    let input = event.target as HTMLInputElement;
    let [hours, mins] = input.value.split(':').map(Number);
    let totalMinutes = hours * 60 + mins;
    this.updateTimeInterval(index, field, totalMinutes);
  }

  @action
  toggleRecurringAnnually() {
    this.formData = {
      ...this.formData,
      recurring_annually: !this.formData.recurring_annually,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'OfficeHours::ExceptionsEditor': typeof ExceptionsEditor;
    'office-hours/exceptions-editor': typeof ExceptionsEditor;
  }
}
